import { DeleteOutlined, PlusCircleFilled } from '@ant-design/icons';
import { Button, Col, Form, Input, Row, Space } from 'antd';
import { FormInstance } from 'antd/lib/form';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { fetchGroupMembers } from '../../../api/groups';
import { fetchUsers } from '../../../api/users';
import { GroupDetails, UserDetails } from '../../../constants/types';
import theme from '../../../styles/theme';
import {
  CheckboxContainer,
  StyledCheckboxItem,
  StyledFormItem,
} from '../style';
import SelectUserModal from './SelectUserModal';
import { StyledAddBtn } from './style';

const { TextArea } = Input;

interface Props {
  loading: boolean;
  groupId: string;
  group: GroupDetails;
  onSubmit: Function;
  cancel: Function;
}

interface UserList {
  email?: string;
  label: string;
  value: string | number;
  isChecked: boolean;
  type: 'New' | 'Old';
  enabled?: boolean;
  account_end_date?: string;
  user_details?: UserDetails;
}

interface FormValues {
  groupName: string;
  groupDescription: string;
}

const EditGroupForm: FC<Props> = ({
  loading,
  groupId,
  group,
  onSubmit,
  cancel,
}) => {
  const formRef = useRef<FormInstance>(null);
  const [form] = Form.useForm();
  const intl = useIntl();
  const initialValues: FormValues = {
    groupName: group?.name!,
    groupDescription: group?.description!,
  };

  const [userList, setUserList] = useState<UserList[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<UserList[]>([]);
  const [isSelectUsersModal, setIsSelectUsersModal] = useState<boolean>(false);

  const selectedUser = useCallback((userListDetails: UserList[]) => {
    const selectedUserList = userListDetails.filter(
      user => user.isChecked === true,
    );
    setSelectedUsers([...selectedUserList]);
  }, []);

  useEffect(() => {
    if (groupId) {
      fetchUsers().then(async users => {
        const groupMember = await fetchGroupMembers(groupId);
        const userListDetails = users.map((user: UserDetails) => {
          const memberIndex = groupMember.findIndex(
            member => member.id === user.id,
          );
          return {
            email: user.email,
            value: user.id,
            label: `${user.firstName} ${user.lastName}`,
            isChecked: !(memberIndex === -1),
            type: memberIndex === -1 ? 'New' : 'Old',
            enabled: user.enabled,
            account_end_date: user?.['attributes']?.['account_end_date']?.[0],
            user_details: user,
          };
        }) as UserList[];
        setUserList([...userListDetails]);
        selectedUser([...userListDetails] as UserList[]);
      });
    }

    return () => {
      setUserList([]);
      setSelectedUsers([]);
    };
  }, [groupId, selectedUser]);

  const onRemove = (index: number) => {
    const data = selectedUsers;
    const userListData: UserList[] = [...userList];
    const userListDataIndex = userListData.findIndex(
      user => user.value === data[index].value,
    );
    if (userListDataIndex !== -1) {
      userListData[userListDataIndex].isChecked = false;
      setUserList([...userListData]);
    }
    data.splice(index, 1);
    setSelectedUsers([...data]);
  };

  const onSelect = (selectedUser: any[]) => {
    const selectedUserId = selectedUser.map((user: UserList) => user.value);
    const userListData: UserList[] = [...userList];
    const updatedData = userListData.map(user => {
      if (!selectedUserId.includes(user.value)) {
        user.isChecked = false;
        return user;
      }
      return user;
    });
    setUserList([...updatedData]);
    setSelectedUsers([...selectedUser]);
    setIsSelectUsersModal(false);
  };

  const onCloseSelectUserModal = () => {
    setIsSelectUsersModal(false);
  };

  const onFinish = (groupDetails: FormValues) => {
    const { groupName, groupDescription } = groupDetails;
    const addedUser = userList
      .filter(user => user.type === 'New' && user.isChecked)
      .map(user => {
        return {
          id: user.value,
          account_end_date: user.account_end_date,
          user_details: user.user_details,
        };
      });
    const deleteUser = userList
      .filter(user => user.type === 'Old' && !user.isChecked)
      .map(user => user.value);
    onSubmit({
      name: groupName.trim(),
      path: `/user-management/groups/${groupName.replace(/ /g, '-').trim()}`,
      subGroups: [],
      description: groupDescription,
      deleteUser,
      addedUser,
    });
  };

  return (
    <>
      <Form
        layout="vertical"
        onFinish={onFinish}
        ref={formRef}
        validateTrigger="onBlur"
        form={form}
        initialValues={initialValues}
        data-testid="edit-group-form"
      >
        <Row gutter={15}>
          <Col span={24}>
            <StyledFormItem
              label={
                <FormattedMessage id="UserGroup.form.label.userGroupName" />
              }
              name="groupName"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: 'UserGroup.form.userGroupName.required',
                  }),
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({
                  id: 'UserGroup.form.placeholder.userGroupName',
                })}
                disabled
                data-testid="group-name"
                maxLength={200}
              />
            </StyledFormItem>
          </Col>
          <Col span={24}>
            <StyledFormItem
              label={<FormattedMessage id="UserGroup.form.label.description" />}
              name="groupDescription"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: 'UserGroup.form.description.required',
                  }),
                },
              ]}
            >
              <TextArea
                maxLength={300}
                placeholder={intl.formatMessage({
                  id: 'UserGroup.form.placeholder.description',
                })}
                data-testid="group-description"
              />
            </StyledFormItem>
          </Col>
          <Col span={24} style={{ marginBottom: '22px' }}>
            <StyledFormItem
              className="only-label"
              label={<FormattedMessage id="UserGroup.form.label.manageUsers" />}
              labelAlign="left"
              colon={false}
            />
            <StyledAddBtn
              shape="round"
              size="large"
              type="default"
              icon={<PlusCircleFilled />}
              className="action-btn"
              onClick={() => setIsSelectUsersModal(true)}
              data-testid="group-addusers"
            >
              <FormattedMessage id="Global.Add" />
            </StyledAddBtn>
          </Col>
          <Col span={24}>
            <CheckboxContainer>
              {selectedUsers.length > 0 &&
                selectedUsers.map((user: any, index: number) => (
                  <StyledCheckboxItem key={index} className="groupmember">
                    {user.label}&nbsp;
                    {user.email && `| ${user.email}`}
                    <DeleteOutlined
                      style={{ color: theme.palette.primary.main }}
                      className="delete-icon"
                      data-testid={`group-deleteuser-${index}`}
                      onClick={() => onRemove(index)}
                    />
                  </StyledCheckboxItem>
                ))}
              {selectedUsers.length === 0 && (
                <StyledCheckboxItem
                  key="empty"
                  className="emptyuserblock"
                  data-testid="group-emptyusers"
                >
                  <FormattedMessage id="UserGroup.list.placeholder" />
                </StyledCheckboxItem>
              )}
            </CheckboxContainer>
          </Col>
        </Row>

        <Row justify="center" style={{ marginTop: '30px' }}>
          <Space size="middle">
            <Button
              type="default"
              shape="round"
              size="large"
              onClick={() => cancel()}
              data-testid="group-edit-cancel"
            >
              <FormattedMessage id="Global.cancel" />
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              shape="round"
              size="large"
              disabled={loading}
              data-testid="group-edit-submit"
            >
              <FormattedMessage id="UserGroup.form.button.submit" />
            </Button>
          </Space>
        </Row>
      </Form>
      <SelectUserModal
        visible={isSelectUsersModal}
        userList={userList}
        selectedList={selectedUsers}
        onSelect={onSelect}
        onCloseModal={onCloseSelectUserModal}
      />
    </>
  );
};

export default EditGroupForm;
